import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconInfoCircle, IconLoader2, IconPlus, IconTrash, IconCopy } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@common/utils';
import useTheme from 'chat-ui/src/hooks/useTheme';
import * as Yup from 'yup';
import DOMPurify from 'dompurify';
import Switch from '@/components/switch/Switch';
import ToolTip from '@/components/tooltip/ToolTip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LangContext } from '@/context/LangProvider';
import { ECompanyThemeName, EProjectStatus } from '@/enums';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import { useMutateProjectUpdate } from '@/reactQuery/post';
import useCreateProjectStore from '@/store/CreateProjectStore';
import toCamelCase from '@/utils/toCamelCase';
import EmbedLinkPanelComponent from './EmbedLinkPanelComponent';
import QrCodePanelComponent from './QrCodePanelComponent';
import { PanelList, PanelListFormData, ProjectPanel } from '@/types';
import ToastMessage from '@/components/toast-message/ToastMessage';
import RequiredLabel from '@/components/labels/RequiredLabel';
import ErrorMessage from '@/components/error-message/ErrorMessage';

const sanitizeInput = (input: string): string => {
  if (!input) return input;

  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

const validationSchema = Yup.object().shape({
  panel: Yup.array()
    .of(
      Yup.object().shape({
        confirmation_link: Yup.string().required('msg.confirmationLinkIsRequired').trim(),
        provider_name: Yup.string().required('msg.providerNameIsRequired').trim(),
        screened_out_link: Yup.string(),
        quota_full_link: Yup.string(),
        poor_quality_link: Yup.string(),
        auto_redirect: Yup.boolean(),
      }),
    )
    .required('msg.atLeastOnePanelIsRequired'),
});

function ProjectSharePanel({ singleProjectData }: ProjectPanel) {
  const { panel: projectPanel = {}, status } = singleProjectData || {};

  const { lang } = useContext(LangContext);
  const {
    project_id: projectId,
    workspace_id: workspaceId,
    organization_id: organizationId,
  } = useParams();
  const { isUserViewer } = useWorkspaceRoles();
  const { setIsFormChanged, isFormChanged } = useCreateProjectStore();
  const { currentTheme, themeName } = useTheme();

  const isIntegrated = projectPanel && Object.keys(projectPanel).length > 0;
  const [isVisible, setIsVisible] = useState(isIntegrated);
  const transformedPanelsData: PanelList[] = Object.values(projectPanel || {});
  const [panelList, setPanelList] = useState<PanelList[]>(transformedPanelsData || []);
  const [projectLink, setProjectLink] = useState('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isProviderNameChanged, setIsProviderNameChanged] = useState(false);
  const interviewLinkRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isProjectPublished = status === EProjectStatus.PUBLISHED;
  const isProjectCompleted = status === EProjectStatus.COMPLETED;
  const disabled = isUserViewer || isProjectCompleted;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<PanelListFormData>({
    defaultValues: { panel: panelList },
    resolver: yupResolver(validationSchema),
  });

  const { mutateAsync } = useMutateProjectUpdate(organizationId, workspaceId, projectId);

  const flashInputAtIndex = (index: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveIndex(index);
    timeoutRef.current = setTimeout(() => setActiveIndex(null), 700);
  };

  const providerNameChangedShowSuccessMessage = () => {
    if (isProviderNameChanged) {
      toast.info(
        <ToastMessage
          title="linkUpdatedSuccessfully"
          description="noteThatTheLinkForThisPanelHasBeenUpdatedAndShouldBeSavedToReflectYourChanges"
        />,
        { toastId: 'linkUpdatedSuccessfully' },
      );
    }
  };

  const copyInterviewLink = async (link: string) => {
    if (interviewLinkRef.current) {
      try {
        await navigator.clipboard.writeText(link);
        toast.info(lang.get('msg.linkCopied'));
      } catch (err) {
        toast.error(lang.get('msg.linkNotCopied'));
      }
    }
  };

  const onSubmit: SubmitHandler<PanelListFormData> = async (data: PanelListFormData) => {
    const providerNames = data.panel.map((panel) => toCamelCase(panel.provider_name));
    const hasDuplicates = providerNames.some(
      (name, index) => providerNames.indexOf(name) !== index,
    );

    if (hasDuplicates) {
      toast.error(lang.get('msg.providersNameMustBeUnique'));
      return;
    }

    const filteredData = data.panel.map((panel) => {
      // Work on a copy so we don't mutate react-hook-form's state.
      const cleaned = { ...panel };

      // Drop empty-string optional link fields so they aren't persisted.
      (Object.keys(cleaned) as (keyof PanelList)[]).forEach((key) => {
        if (cleaned[key] === '') {
          delete cleaned[key];
        }
      });

      return cleaned;
    });

    const formattedData = filteredData.reduce(
      (acc, panel) => {
        acc[toCamelCase(panel.provider_name)] = panel;
        return acc;
      },
      {} as { [key: string]: PanelList },
    );

    try {
      await mutateAsync({ panel: formattedData });
      setIsFormChanged(false);
      setIsProviderNameChanged(false);
      if (isProjectPublished) {
        toast.success(lang.get('msg.panelIntegrationSuccessfullyUpdated'));
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessage = getErrorMessage(axiosError);
      toast.error(errorMessage);
    }
  };

  const addPanelHandler = () => {
    const newPanel = {
      confirmation_link: '',
      provider_name: '',
    };

    setPanelList((prevList) => [...prevList, newPanel]);
    setIsFormChanged(true);
  };

  const removePanelHandler = (index: number) => {
    const updatedPanelList = panelList.filter((_, i) => i !== index);
    setPanelList(updatedPanelList);
    setValue('panel', updatedPanelList);
    setIsFormChanged(true);
  };

  const handleToggle = async () => {
    try {
      if (isIntegrated) {
        await mutateAsync({ panel: {}, project_id: projectId });
        setPanelList([]);
      }
      if (!isIntegrated && panelList.length === 0) {
        addPanelHandler();
      }
      const hasChanges = panelList.some((panel) => Object.values(panel).some((value) => !!value));
      setIsFormChanged(hasChanges);
      setIsVisible(!isVisible);
      setIsProviderNameChanged(false);
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessage = getErrorMessage(axiosError);
      toast.error(errorMessage);
    }
  };

  const updatePanelField = (index: number, field: keyof PanelList, value: string | boolean) => {
    const sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;

    setPanelList((prevList) =>
      prevList.map((panel, i) => (i === index ? { ...panel, [field]: sanitizedValue } : panel)),
    );
    setIsFormChanged(true);
  };

  useEffect(() => {
    setValue('panel', panelList);
  }, [panelList, setValue]);

  useEffect(() => {
    const { VITE_ENVIRONMENT_CHAT, VITE_ENVIRONMENT_CHAT_EMPATHY } = import.meta.env;

    const baseUrl =
      themeName === ECompanyThemeName.EMPATHY
        ? VITE_ENVIRONMENT_CHAT_EMPATHY
        : VITE_ENVIRONMENT_CHAT;

    setProjectLink(`${baseUrl}/${projectId}`);
  }, [themeName, projectId]);

  return (
    <div className="w-full h-full">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full gap-6 pb-10">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1 w-fit">
                <ToolTip text="Select panel integration" className="ml-5" />
                <p className="flex items-center gap-1 mr-2 text-base font-medium">
                  {lang.get('msg.panelIntegration')}
                </p>
                <Switch func={handleToggle} value={isVisible} isDisabled={disabled} />
              </div>
              {isVisible && (
                <p className="flex items-center gap-1 text-sm">
                  <IconInfoCircle size={15} />
                  {lang.get('msg.panelIntegrationDescription')}
                </p>
              )}
            </div>
          </div>
        </div>

        {isVisible && (
          <div className="flex flex-col w-full gap-5">
            <div
              className="flex flex-col py-5 rounded-lg"
              style={{ backgroundColor: currentTheme?.['secondary-background'] }}
            >
              {React.Children.toArray(
                panelList?.map((item, index) => {
                  const borderColor = activeIndex === index ? currentTheme?.primary : '';
                  return (
                    <div className="flex flex-col w-full gap-3 px-6 py-4 border-b border-gray-200 last:border-0">
                      <div className="flex flex-col w-full gap-4">
                        {/* Input fields section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 items-start w-full gap-4 text-dark-text">
                          {/* -- provider name -- */}
                          <RequiredLabel title="providerName">
                            <Input
                              onKeyDown={() => {
                                flashInputAtIndex(index);
                                setIsProviderNameChanged(true);
                              }}
                              hasError={!!errors.panel?.[index]?.provider_name?.message}
                              placeholder={lang.get('msg.providerName')}
                              {...register(`panel.${index}.provider_name`, {
                                onChange: (e) =>
                                  updatePanelField(index, 'provider_name', e.target.value),
                              })}
                              onBlur={providerNameChangedShowSuccessMessage}
                              disabled={disabled}
                            />
                            <ErrorMessage message={errors.panel?.[index]?.provider_name?.message} />
                          </RequiredLabel>
                          {/* -- confirmation link -- */}
                          <RequiredLabel title="confirmationLink">
                            <Input
                              hasError={!!errors.panel?.[index]?.confirmation_link?.message}
                              placeholder={lang.get('msg.confirmationLink')}
                              {...register(`panel.${index}.confirmation_link`, {
                                onChange: (e) =>
                                  updatePanelField(index, 'confirmation_link', e.target.value),
                              })}
                              disabled={disabled}
                            />
                            <ErrorMessage
                              message={errors.panel?.[index]?.confirmation_link?.message}
                            />
                          </RequiredLabel>
                          {/* -- screen out link -- */}
                          <RequiredLabel title="screenedOutLink" notRequired>
                            <Input
                              placeholder={lang.get('msg.screenedOutLink')}
                              disabled={disabled}
                              style={{ borderColor }}
                              {...register(`panel.${index}.screened_out_link`, {
                                onChange: (e) =>
                                  updatePanelField(index, 'screened_out_link', e.target.value),
                              })}
                            />
                          </RequiredLabel>
                          {/* -- quota full link -- */}
                          <RequiredLabel title="quotaFullLink" notRequired>
                            <Input
                              placeholder={lang.get('msg.quotaFullLink')}
                              disabled={disabled}
                              style={{ borderColor }}
                              {...register(`panel.${index}.quota_full_link`, {
                                onChange: (e) =>
                                  updatePanelField(index, 'quota_full_link', e.target.value),
                              })}
                            />
                          </RequiredLabel>
                          {/* -- poor quality link -- */}
                          <RequiredLabel title="poorQualityLink" notRequired>
                            <Input
                              placeholder={lang.get('msg.poorQualityLink')}
                              disabled={disabled}
                              style={{ borderColor }}
                              {...register(`panel.${index}.poor_quality_link`, {
                                onChange: (e) =>
                                  updatePanelField(index, 'poor_quality_link', e.target.value),
                              })}
                            />
                          </RequiredLabel>
                          {/* -- interview link -- */}
                          <RequiredLabel title="interviewLink" notRequired>
                            <Input
                              readOnly
                              value={`${projectLink}?source=${toCamelCase(item.provider_name)}`}
                              placeholder={lang.get('msg.interviewLink')}
                              ref={interviewLinkRef}
                              disabled={disabled}
                              style={{ borderColor }}
                            />
                          </RequiredLabel>
                        </div>

                        {/* Actions section at the bottom */}
                        <div className="flex items-center gap-4 pt-4">
                          {/* Left column - Auto redirect toggle */}
                          <div className="flex items-center gap-2 flex-1">
                            <p
                              style={{ color: currentTheme?.['primary-text'] }}
                              className="text-sm font-medium"
                            >
                              {lang.get('msg.autoRedirect')}
                            </p>
                            <Switch
                              func={() =>
                                updatePanelField(index, 'auto_redirect', !item.auto_redirect)
                              }
                              value={item.auto_redirect || false}
                              isDisabled={disabled}
                            />
                          </div>

                          {/* Right column - Action buttons */}
                          <div className="flex items-center justify-between gap-2 flex-1">
                            {/* Left aligned - Copy, QR, Embed */}
                            <div className="flex items-center gap-2">
                              <Button
                                variant="default"
                                onClick={() =>
                                  copyInterviewLink(
                                    `${projectLink}?source=${toCamelCase(item.provider_name)}`,
                                  )
                                }
                                className="border-none"
                                type="button"
                                title="Copy interview link"
                              >
                                <IconCopy size={20} />
                              </Button>
                              {/* qr code button */}
                              <QrCodePanelComponent
                                projectLink={projectLink}
                                providerName={item?.provider_name}
                              />

                              {/* embed button */}
                              <EmbedLinkPanelComponent
                                projectLink={projectLink}
                                providerName={item?.provider_name}
                                index={index}
                              />
                            </div>

                            {/* Right side - Delete button */}
                            {!disabled && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removePanelHandler(index)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2"
                                type="button"
                              >
                                <IconTrash size={20} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }),
              )}
            </div>
            {!disabled && (
              <div className="flex items-center w-full gap-3">
                <Button
                  type="button"
                  onClick={addPanelHandler}
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  className="w-full text-dark-text"
                >
                  <IconPlus size="1rem" />
                  {lang.get('msg.addPanel')}
                </Button>
                {isFormChanged && (
                  <Button variant="default" size="sm" className="w-full" type="submit">
                    {isSubmitting && <IconLoader2 className="w-5 h-5 animate-spin" />}
                    {lang.get('msg.save')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

export default ProjectSharePanel;
